import { useCallback, useEffect, useMemo, useState } from 'react';
import { accountEnvironmentMatches } from '../../../shared/appSession';
import {
  defaultKey, kontoAnzahl, ladeAppZugaenge, zugaengeFuerSatz,
  type AppCreds, type Freigabe, type ProdUser,
} from './appZugaenge';
import { useAuth } from '../../contexts/AuthContext';

/*
 * LOGIN-HILFE AUSSERHALB VON ELECTRON (Rafael, 05.10.2026, Buehne
 * b-login-addin-ipad-20261005, Wahl a)
 * ------------------------------------------------------------------
 * Im Browser (iPad-Safari, normaler Browser) ist das Fenster ein
 * seitenfremdes <iframe>: hineingreifen, Felder fuellen oder die Adresse
 * mitlesen geht nicht. Der Electron-Zusatz (AppLoginPanel) meldet per
 * executeJavaScript an — das gibt es hier nicht. Diese Hilfe zeigt deshalb nur
 * das passende Konto und legt E-Mail bzw. Passwort in die Zwischenablage; das
 * Einfuegen ins Anmeldefeld macht der Mensch.
 *
 * Bewusst eingeklappt: ob das Fenster gerade die Login-Maske zeigt, ist im
 * Browser unbekannt (die iframe-Adresse ist nicht lesbar). Ein Dauer-Overlay
 * laege also auch ueber der angemeldeten App.
 */

interface Props {
  appKey: string;
  credentialSet: 'production' | 'dev';
  /** Kuerzel aus Rafaels Freigabeliste (Guide `konfig.konten`), sonst null. */
  konto?: string | null;
  /** Vercel-Staging — sperrt fuer Partner Engelmann-Staging (siehe AppLoginPanel). */
  isStaging?: boolean;
}

interface Konto { email: string; password: string; name: string | null; anzahl?: string | null }

const TOUCH = 44;

export default function AppLoginHilfe({ appKey, credentialSet, konto, isStaging = false }: Props) {
  const { user: angemeldet, authEnabled } = useAuth();
  const gesperrt = isStaging && appKey === 'engelmann-ai-hub' && authEnabled === true && !!angemeldet && angemeldet.role !== 'admin';
  const [offen, setOffen] = useState(false);
  const [apps, setApps] = useState<Record<string, AppCreds> | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [prodKonten, setProdKonten] = useState<ProdUser[] | null>(null);
  const [freigabe, setFreigabe] = useState<Freigabe | null | 'unbekannt'>(null);
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [pwSichtbar, setPwSichtbar] = useState(false);
  const [kopiert, setKopiert] = useState<'email' | 'passwort' | null>(null);
  const [kopierFehler, setKopierFehler] = useState<string | null>(null);
  const [neuLaden, setNeuLaden] = useState(0);
  const istProdSatz = credentialSet === 'production';

  // Zugangsdaten erst beim Aufklappen holen — eingeklappt braucht es sie nicht.
  useEffect(() => {
    if (!offen) return;
    let aktiv = true;
    setLadeFehler(null);
    const laden = async () => {
      if (istProdSatz) {
        // Produktionspasswoerter liegen nur im Prod-Verzeichnis (Betreiber).
        // Ein 403 fuer Partner ist gewollt und wird angezeigt, nicht verschluckt.
        const r = await fetch(`/api/app-prod-users/${encodeURIComponent(appKey)}`);
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`);
        if (aktiv) setProdKonten(Array.isArray(d.users) ? d.users : []);
      } else {
        const a = await ladeAppZugaenge();
        if (aktiv) setApps(a);
      }
    };
    laden().catch(e => { if (aktiv) setLadeFehler(e instanceof Error ? e.message : String(e)); });
    return () => { aktiv = false; };
  }, [offen, istProdSatz, appKey, neuLaden]);

  useEffect(() => {
    if (!konto || !offen) { if (!konto) setFreigabe(null); return; }
    let aktiv = true;
    fetch('/api/guide/konten')
      .then(r => (r.ok ? r.json() : { konten: [] }))
      .then((d: { konten?: Freigabe[] }) => {
        if (aktiv) setFreigabe((d.konten ?? []).find(k => k.kuerzel === konto) ?? 'unbekannt');
      })
      .catch(() => { if (aktiv) setFreigabe('unbekannt'); });
    return () => { aktiv = false; };
  }, [konto, offen]);

  // Dieselbe Auswahl wie im Electron-Zusatz: nur Konten des passenden Satzes,
  // und nur solche, deren Passwort wir kennen — sonst gaebe es nichts zu kopieren.
  const kandidaten: Konto[] = useMemo(() => {
    if (istProdSatz) {
      return (prodKonten ?? [])
        .filter(u => u.canLogin && u.password)
        .map(u => ({ email: u.email, password: u.password as string, name: u.name }));
    }
    const app = apps?.[appKey];
    if (!app || gesperrt) return [];
    // Konten mit Daten zuerst und mit Anzahl (Rafael, 05.10.2026).
    // Nach Anzahl, damit der Vorschlag (erstes Konto) das mit den meisten Projekten ist.
    const hat = (u: { daten?: unknown }) => kontoAnzahl(u as never) ?? 0;
    return zugaengeFuerSatz(app.users, credentialSet)
      .filter(u => u.password)
      .sort((a, b) => hat(b) - hat(a) || (b.primary ? 1 : 0) - (a.primary ? 1 : 0) || (a.name || a.email).localeCompare(b.name || b.email))
      .map(u => {
        const d = Object.values(u.daten ?? {}).find(x => 'anzahl' in x && x.anzahl > 0) as { anzahl: number; was: string } | undefined;
        return { email: u.email, password: u.password as string, name: u.name, anzahl: d ? `${d.anzahl} ${d.was}` : null };
      });
  }, [istProdSatz, prodKonten, apps, appKey, credentialSet, gesperrt]);

  // Freigabe aus dem Guide passt nur, wenn App und Umgebung stimmen.
  const freigabeHinweis = useMemo(() => {
    if (!konto || freigabe === null) return null;
    if (freigabe === 'unbekannt') return `Konto "${konto}" steht nicht in der Freigabeliste.`;
    if (freigabe.app && freigabe.app !== appKey) return `Konto "${konto}" gehört zu ${freigabe.app}, nicht zu dieser App.`;
    if (!accountEnvironmentMatches(freigabe.umgebung, credentialSet)) return 'Das freigegebene Konto gehört zu einer anderen Umgebung.';
    if (!kandidaten.some(k => k.email === freigabe.email)) return `Für das freigegebene Konto (${freigabe.email}) ist hier kein Passwort hinterlegt.`;
    return null;
  }, [konto, freigabe, appKey, credentialSet, kandidaten]);

  const vorschlag = useMemo(() => {
    if (freigabe && freigabe !== 'unbekannt' && !freigabeHinweis) return freigabe.email;
    const standard = localStorage.getItem(defaultKey(appKey));
    if (standard && kandidaten.some(k => k.email === standard)) return standard;
    return kandidaten[0]?.email ?? null;
  }, [freigabe, freigabeHinweis, appKey, kandidaten]);

  const aktuell = kandidaten.find(k => k.email === (gewaehlt ?? vorschlag)) ?? null;

  const kopiere = useCallback(async (was: 'email' | 'passwort', text: string) => {
    setKopierFehler(null);
    setKopiert(null);
    const bezeichnung = was === 'email' ? 'E-Mail' : 'Passwort';
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Zwischenablage in diesem Browser nicht verfügbar');
      await navigator.clipboard.writeText(text);
    } catch (e) {
      const grund = e instanceof Error ? e.message : String(e);
      setKopierFehler(`${bezeichnung} konnte nicht kopiert werden (${grund}).`
        + (was === 'passwort' ? ' Passwort einblenden und von Hand übernehmen.' : ''));
      return;
    }
    setKopiert(was);
    window.setTimeout(() => setKopiert(k => (k === was ? null : k)), 1500);
  }, []);

  if (!offen) {
    return (
      <button
        type="button"
        data-ai-id="app-login-hilfe-griff"
        onClick={() => setOffen(true)}
        title="Login-Hilfe: Zugangsdaten zum Einfügen kopieren"
        style={{
          position: 'absolute', top: 44, right: 0, zIndex: 20,
          minWidth: TOUCH, minHeight: TOUCH,
          background: 'var(--tn-bg-dark)', color: 'var(--tn-text)',
          border: '1px solid var(--tn-border)', borderRight: 'none',
          borderRadius: '6px 0 0 6px', padding: '6px 8px', cursor: 'pointer', fontSize: 12,
        }}
      >
        🔑 Login
      </button>
    );
  }

  const knopfStil: React.CSSProperties = {
    flex: 1, minHeight: TOUCH, borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    background: 'var(--tn-bg)', color: 'var(--tn-text)', border: '1px solid var(--tn-border)',
  };

  return (
    <div
      data-ai-id="app-login-hilfe"
      style={{
        position: 'absolute', top: 38, right: 0, zIndex: 20, width: 300, maxWidth: '100%',
        display: 'flex', flexDirection: 'column', gap: 8, padding: 10,
        background: 'var(--tn-surface)', border: '1px solid var(--tn-border)', borderRight: 'none',
        borderRadius: '0 0 0 8px', boxShadow: '-4px 4px 12px rgba(0,0,0,0.3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tn-text)' }}>
          🔑 {apps?.[appKey]?.name ?? appKey}{istProdSatz ? ' · Produktion' : ''}
        </span>
        <button
          type="button"
          onClick={() => setOffen(false)}
          title="Einklappen"
          aria-label="Login-Hilfe einklappen"
          style={{ minWidth: TOUCH, minHeight: TOUCH, background: 'none', border: 'none', color: 'var(--tn-text-muted)', cursor: 'pointer', fontSize: 18 }}
        >×</button>
      </div>

      {ladeFehler && (
        <div role="alert" style={{ fontSize: 12, color: 'var(--tn-red)' }}>
          Zugangsdaten nicht geladen: {ladeFehler}
          <button type="button" onClick={() => setNeuLaden(n => n + 1)}
            style={{ marginLeft: 8, minHeight: TOUCH, padding: '0 10px', background: 'none', color: 'var(--tn-text)', border: '1px solid var(--tn-border)', borderRadius: 6, cursor: 'pointer' }}>
            Erneut laden
          </button>
        </div>
      )}

      {freigabeHinweis && (
        <div role="status" style={{ fontSize: 12, color: 'var(--tn-warn)' }}>{freigabeHinweis}</div>
      )}

      {!ladeFehler && (istProdSatz ? prodKonten === null : apps === null) && (
        <div style={{ fontSize: 12, color: 'var(--tn-text-muted)' }}>Zugangsdaten werden geladen …</div>
      )}

      {gesperrt && (
        <div role="status" data-ai-id="app-login-gesperrt" style={{ fontSize: 12, color: 'var(--tn-text-muted)' }}>
          Engelmann-Staging ist für dich nicht freigegeben: dort liegen echte Kundendaten.
        </div>
      )}
      {!gesperrt && !ladeFehler && (istProdSatz ? prodKonten !== null : apps !== null) && kandidaten.length === 0 && (
        <div role="status" style={{ fontSize: 12, color: 'var(--tn-text-muted)' }}>
          Für diese App und Umgebung ist kein Konto mit Passwort hinterlegt.
        </div>
      )}

      {aktuell && (
        <>
          {kandidaten.length > 1 && (
            <select
              data-ai-id="app-login-hilfe-auswahl"
              value={aktuell.email}
              onChange={e => { setGewaehlt(e.target.value); setPwSichtbar(false); setKopierFehler(null); }}
              style={{ minHeight: TOUCH, fontSize: 13, background: 'var(--tn-bg)', color: 'var(--tn-text)', border: '1px solid var(--tn-border)', borderRadius: 6, padding: '0 8px' }}
            >
              {kandidaten.map(k => <option key={k.email} value={k.email}>{k.anzahl ? `● ${k.anzahl} · ` : ''}{k.name ? `${k.name} — ${k.email}` : k.email}</option>)}
            </select>
          )}
          <div style={{ fontSize: 13, color: 'var(--tn-text)', wordBreak: 'break-all' }} data-ai-id="app-login-hilfe-email">
            {aktuell.email}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span data-ai-id="app-login-hilfe-passwort" style={{ flex: 1, fontFamily: 'monospace', fontSize: 13, color: 'var(--tn-text)', wordBreak: 'break-all' }}>
              {pwSichtbar ? aktuell.password : '••••••••'}
            </span>
            <button type="button" onClick={() => setPwSichtbar(v => !v)}
              style={{ minHeight: TOUCH, padding: '0 10px', fontSize: 12, background: 'none', color: 'var(--tn-text-muted)', border: '1px solid var(--tn-border)', borderRadius: 6, cursor: 'pointer' }}>
              {pwSichtbar ? 'verbergen' : 'zeigen'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" data-ai-id="app-login-hilfe-email-kopieren"
              onClick={() => void kopiere('email', aktuell.email)} style={knopfStil}>
              {kopiert === 'email' ? 'kopiert ✓' : 'E-Mail kopieren'}
            </button>
            <button type="button" data-ai-id="app-login-hilfe-passwort-kopieren"
              onClick={() => void kopiere('passwort', aktuell.password)} style={knopfStil}>
              {kopiert === 'passwort' ? 'kopiert ✓' : 'Passwort kopieren'}
            </button>
          </div>
          {kopierFehler && (
            <div role="alert" data-ai-id="app-login-hilfe-fehler" style={{ fontSize: 12, color: 'var(--tn-red)' }}>{kopierFehler}</div>
          )}
          <div style={{ fontSize: 11, color: 'var(--tn-text-muted)' }}>
            In das Anmeldefeld im Fenster einfügen.
          </div>
        </>
      )}
    </div>
  );
}
